/**
 * Map raw V3 status messages to seller-friendly copy.
 *
 * V3 publishes verbose technical events for operator debugging:
 *   - "fill: filled nth=0 via #username (ref=linkedin_email)"
 *   - "click: clicked nth=0 via button[data-litms-control-urn=...]"
 *   - "V3 driver starting — goal: Sign in to LinkedIn..."
 * Iter-17 collapses these into friendly strings or drops them.
 *
 * Returns:
 *   string — friendly text to show
 *   null   — drop this update entirely
 */
export default function humanizeStatusMessage(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s) return null;

  // Raw V3 agent actions — operator-debug events the seller never needs.
  if (/^(fill|click|navigate|press|wait_ms|select|hover|scroll):/i.test(s)) return null;

  // Specific friendly mappings (most-specific first).
  // iter-21: operator directive — never use "Logging in" / "Login" framing
  // in seller-facing copy. Use credential / eligibility verification instead.
  if (/^v3 driver starting/i.test(s)) return 'Verifying credentials';
  if (/^launching browser/i.test(s)) return 'Setting up secure session...';
  if (/^browser launched/i.test(s)) return null;
  if (/agent thinking/i.test(s)) return 'Verifying eligibility';
  if (/code received/i.test(s)) return 'Validating code';
  if (/code accepted/i.test(s)) return 'Code accepted';
  if (/code rejected/i.test(s)) return 'Code rejected. Please re-enter';
  if (/login successful/i.test(s)) return 'Verification complete';
  if (/^logging in/i.test(s) || /^starting automated login/i.test(s)) return 'Verifying credentials';
  if (/email change complete/i.test(s) || /account update complete/i.test(s)) return 'Account update complete';
  if (/reading verification link/i.test(s) || /inbox.reader/i.test(s)) return 'Validating takeover...';
  if (/two.factor authentication required/i.test(s)) return 'Two-factor authentication required';
  if (/email verification code required/i.test(s) || /linkedin sent a code/i.test(s)) return 'Email verification code required';
  if (/mobile approval required/i.test(s) || /open the linkedin app/i.test(s)) return 'Mobile approval required';
  if (/security verification|security challenge/i.test(s)) {
    return /complete/i.test(s) ? 'Security verification complete' : 'Security verification required';
  }
  if (/finaliz/i.test(s) || /finalising/i.test(s)) return 'Finalizing profile data...';

  // Catch-all filter: anything that looks like a CSS selector, URL,
  // attribute matcher, or V3 internal plumbing — drop it.
  if (/[#\[\]]/.test(s)) return null;
  if (/https?:\/\//i.test(s)) return null;
  if (/value_ref=/i.test(s)) return null;
  if (/nth=\d/i.test(s)) return null;

  // Otherwise pass through.
  return s;
}
